import { CONTACT_CONSTANTS } from "@/features/survey/components/coverSection/constants";
import type {
  ContactItem,
  ContactSearchResult,
  SurveyContact,
} from "@survey-types/survey.contacts.types";
import { useState } from "react";

/**
 * Hook for managing contact search functionality
 */
export const useContactSearch = (
  allContacts: readonly ContactItem[],
  existingContacts: readonly SurveyContact[],
) => {
  const [searchValue, setSearchValue] = useState("");

  const existingEmails = new Set(
    existingContacts
      .map((contact) => contact.email?.toLowerCase())
      .filter(Boolean),
  );

  const searchSuggestions =
    searchValue.length <= CONTACT_CONSTANTS.SEARCH.MIN_LENGTH
      ? []
      : allContacts
          .filter(
            (item) =>
              (item.name + item.email)
                .toLowerCase()
                .includes(searchValue.toLowerCase()) &&
              !existingEmails.has(item.email.toLowerCase()),
          )
          .slice(0, CONTACT_CONSTANTS.SEARCH.MAX_SUGGESTIONS)
          .map(
            (item): ContactSearchResult => ({
              label: `${item.name} (${item.email})`,
              value: item.email,
              name: item.name,
              email: item.email,
              title: item.title,
              phone: item.phone,
            }),
          );

  const noResultsOption =
    searchValue.length > CONTACT_CONSTANTS.SEARCH.MIN_LENGTH &&
    searchSuggestions.length === 0
      ? [
          {
            label: `Sorry, no users found matching ${searchValue}.`,
            value: "no-results",
          },
        ]
      : [];

  const handleSearchChange = (_event: unknown, value: string) => {
    setSearchValue(value);
  };

  const clearSearch = () => {
    setSearchValue("");
  };

  return {
    searchValue,
    searchSuggestions,
    noResultsOption,
    handleSearchChange,
    clearSearch,
  };
};
